/**
 * NiV Clinic Detail — "ניהול" tab (Oct 2026): independent blocks, each with
 * its own save.
 *
 * Column A: payment status · payments log · plan · limits · seats
 * Column B: tags & contact log · free notes · next action · AI chat summary · updates thread
 */

import { ChatText } from "@phosphor-icons/react";
import {
  useAddNivCrmNoteMutation,
  useUpdateNivCrmFreeNotesMutation,
  useUpdateNivCrmNextActionMutation,
} from "../../../../../store/services/nivApi";
import NivNotesThread from "../../shared/NivNotesThread";
import { PaymentStatusCard, PaymentsLogCard, SeatsCard } from "../../shared/ClinicPaymentPanel";
import FreeNotesCard from "../../shared/FreeNotesCard";
import NextActionCard from "../../shared/NextActionCard";
import AiChatSummaryCard from "../../shared/AiChatSummaryCard";
import usePlanOverrides from "../usePlanOverrides";
import PlanCard from "../components/PlanCard";
import LimitsCard from "../components/LimitsCard";
import GrowCard from "../components/GrowCard";
import TagsContactCard from "../components/TagsContactCard";

const scrollToBlock = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
};

export default function ManageTab({ clinic, crm, therapistName, showError, focusPaymentToken, refetch }) {
  const [addNote, { isLoading: isAddingNote }] = useAddNivCrmNoteMutation();
  const [saveFreeNotes] = useUpdateNivCrmFreeNotesMutation();
  const [saveNextAction] = useUpdateNivCrmNextActionMutation();
  // One shared draft: the plan card picks the plan, the limits card edits the
  // sheet against that plan's defaults.
  const overrides = usePlanOverrides(clinic);

  const handleAddNote = async (text) => {
    if (!clinic) return false;
    try {
      await addNote({ clinicId: clinic.clinicId, text, author: therapistName }).unwrap();
      return true;
    } catch (error) {
      console.error("[CK-9862] Error adding clinic CRM note:", {
        errorCode: "CK-9862",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת העדכון");
      return false;
    }
  };

  const handleSaveFreeNotes = async (text) => {
    try {
      await saveFreeNotes({ clinicId: clinic.clinicId, text }).unwrap();
    } catch (error) {
      console.error("[CK-9805] Error saving clinic free notes:", {
        errorCode: "CK-9805",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  };

  const handleSaveNextAction = async ({ text, at }) => {
    try {
      await saveNextAction({ clinicId: clinic.clinicId, text, at }).unwrap();
    } catch (error) {
      console.error("[CK-9819] Error saving clinic next action:", {
        errorCode: "CK-9819",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  };

  if (!clinic) return null;

  return (
    <div className="ncd2-panel">
      <div className="ncd2-two-col ncd2-two-col-equal">
        <div className="ncd2-col">
          <PaymentStatusCard clinic={clinic} crm={crm} onGoToPlan={() => scrollToBlock("ncd2-block-plan")} />
          <PaymentsLogCard clinic={clinic} crm={crm} focusPaymentToken={focusPaymentToken} />
          <PlanCard clinic={clinic} overrides={overrides} refetch={refetch} />
          <LimitsCard clinic={clinic} overrides={overrides} refetch={refetch} />
          <GrowCard clinic={clinic} refetch={refetch} />
          <SeatsCard clinic={clinic} crm={crm} />
        </div>

        <div className="ncd2-col">
          <TagsContactCard clinic={clinic} crm={crm} author={therapistName || "NiV"} />

          <FreeNotesCard
            value={crm?.freeNotes}
            updatedAt={crm?.freeNotesUpdatedAt}
            onSave={handleSaveFreeNotes}
            placeholder="הערות חופשיות על הקליניקה — נשמר אוטומטית"
          />

          <NextActionCard value={crm?.nextAction} onSave={handleSaveNextAction} />

          <AiChatSummaryCard target={{ kind: "clinic", id: clinic.clinicId }} aiSummary={crm?.aiSummary} />

          <section className="ncd2-card">
            <header className="ncd2-card-head">
              <span className="ncd2-card-icon">
                <ChatText size={15} weight="bold" />
              </span>
              <div className="ncd2-card-titles">
                <span className="ncd2-card-eyebrow">פנימי</span>
                <h3 className="ncd2-card-title">
                  עדכונים
                  {crm?.notes?.length > 0 && <span className="ncd2-card-count">{crm.notes.length}</span>}
                </h3>
              </div>
            </header>
            <NivNotesThread notes={crm?.notes || []} onAdd={handleAddNote} isAdding={isAddingNote} emptyLabel="אין עדכונים עדיין" />
          </section>
        </div>
      </div>
    </div>
  );
}
